import React, { useMemo } from 'react';
import { useGetMyTenants } from '@area-restrita/api-client/endpoints/auth/auth.gen';

import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

interface ITenantOption {
  slug: string;
  companyId?: number;
}

interface IProps {
  value?: ITenantOption;
  onChange?: (option?: ITenantOption) => void;
  className?: string;
}

const MyTenantSelector: React.FC<IProps> = ({ value, onChange, className }) => {
  const query = useGetMyTenants();

  const tenants = useMemo(() => query.data ?? [], [query.data]);

  const internalValue = useMemo(() => {
    if (!value) return undefined;

    return value.companyId
      ? `tenant:${value.slug}:company:${value.companyId}`
      : `tenant:${value.slug}`;
  }, [value]);

  return (
    <Select
      value={internalValue}
      onValueChange={(v) => {
        const [_, tenantSlug, __, companyId] = v.split(':');
        const tenant = tenants.find((t) => t.slug === tenantSlug);

        if (tenant) {
          if (companyId) {
            const company = tenant.companies.find(
              (c) => c.id === Number(companyId),
            );
            onChange?.({ slug: tenantSlug, companyId: company?.id });
          } else {
            onChange?.({ slug: tenantSlug });
          }
        }
      }}
    >
      <SelectTrigger className={cn('w-full', className)}>
        <SelectValue placeholder="Selecione uma organização..." />
      </SelectTrigger>
      <SelectContent className="max-h-[300px]">
        {tenants.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <p className="text-sm text-muted-foreground">
              Nenhuma organização encontrada
            </p>
          </div>
        ) : (
          tenants.map((tenant) => (
            <SelectGroup key={tenant.slug}>
              {!tenant.isTenantUser && (
                <SelectLabel className="font-semibold">
                  {tenant.name}
                </SelectLabel>
              )}

              {/* Option to select the tenant itself - only shown if isTenantUser is true */}
              {tenant.isTenantUser && (
                <SelectItem
                  value={`tenant:${tenant.slug}`}
                  className="relative flex items-center font-semibold"
                >
                  <div className="flex items-center">
                    <span>{tenant.name}</span>
                    <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                      Admin
                    </span>
                  </div>
                </SelectItem>
              )}

              {/* Options to select individual companies */}
              {tenant.companies.map((company) => (
                <SelectItem
                  key={company.id}
                  value={`tenant:${tenant.slug}:company:${company.id}`}
                  className="pl-10"
                >
                  {company.tradingName}
                </SelectItem>
              ))}
            </SelectGroup>
          ))
        )}
      </SelectContent>
    </Select>
  );
};

export default MyTenantSelector;
